import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ArrowRight, ChevronDown, Download, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

export type ExportFormat = "xlsx" | "pdf";

/**
 * Icon-chip tint families. Each maps to a tasteful, consistent Tailwind pair so
 * rows of the same report family read alike (e.g. driver/trip = brand blue,
 * cost/savings = emerald, archived = amber). Kept as static class strings so no
 * inline styles are needed.
 */
export type ReportTint = "brand" | "emerald" | "amber";

const TINT_CLASSES: Record<ReportTint, string> = {
  brand: "bg-brand-primary/10 text-brand-primary",
  emerald: "bg-emerald-500/10 text-emerald-600",
  amber: "bg-amber-500/10 text-amber-600",
};

interface Props {
  /** Localized row title. */
  title: string;
  /** Lucide icon shown inside the tinted chip. */
  icon: LucideIcon;
  /**
   * "download" — shows a single "Download ▾" split-button whose menu offers
   * XLSX / PDF; both call `onDownload` with the chosen format.
   * "open" — shows a single "Open →" button that calls `onOpen`.
   */
  variant: "download" | "open";
  /** Icon-chip tint family; defaults to the brand blue. */
  tint?: ReportTint;
  /** Download handler; receives the chosen export format. */
  onDownload?: (format: ExportFormat) => Promise<void> | void;
  /** Open handler for the "open" variant. */
  onOpen?: () => void;
}

/**
 * A single report entry in the export hub, rendered as a compact ~56px LIST ROW
 * (Sprint 119, TASK-3846 — de-clutter the Reports hub).
 *
 * Each row carries a small tinted icon chip (`size-7`), the report title, and a
 * single trailing action on the right. "download" rows expose ONE "Download ▾"
 * split-button (a Radix DropdownMenu offering XLSX / PDF — replacing the former
 * two separate buttons); "open" rows expose a single "Open →" navigation button.
 * Behaviour is preserved exactly: each menu item calls the same `onDownload`
 * with the same format the previous per-button handlers used. While a download
 * is in flight the trigger is disabled and shows a spinner; failures surface as
 * inline error text under the title.
 */
export function ReportExportCard({
  title,
  icon: Icon,
  variant,
  tint = "brand",
  onDownload,
  onOpen,
}: Props) {
  const { t } = useTranslation();
  const [busyFormat, setBusyFormat] = useState<ExportFormat | null>(null);
  const [error, setError] = useState(false);

  async function handleDownload(format: ExportFormat) {
    if (!onDownload || busyFormat !== null) return;
    setBusyFormat(format);
    setError(false);
    try {
      await onDownload(format);
    } catch {
      setError(true);
    } finally {
      setBusyFormat(null);
    }
  }

  return (
    <div className="flex min-h-[56px] items-center gap-3 px-3 py-2">
      <span
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-lg",
          TINT_CLASSES[tint],
        )}
      >
        <Icon size={15} aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-800">{title}</p>
        {error && (
          <p className="mt-0.5 text-xs text-brand-danger" role="alert">
            {t("analytics.hub.exportError")}
          </p>
        )}
      </div>

      {variant === "open" ? (
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
        >
          {t("analytics.hub.openReport")}
          <ArrowRight size={14} aria-hidden />
        </button>
      ) : (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              disabled={busyFormat !== null}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busyFormat !== null ? (
                <Loader2 size={14} className="animate-spin" aria-hidden />
              ) : (
                <Download size={14} aria-hidden />
              )}
              {t("analytics.hub.downloadAction")}
              <ChevronDown size={14} aria-hidden />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={4}
              className="z-50 min-w-[120px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
            >
              <DropdownMenu.Item
                onSelect={() => void handleDownload("xlsx")}
                className="flex cursor-pointer items-center rounded-md px-3 py-2 text-sm text-gray-700 outline-none hover:bg-gray-50 focus:bg-gray-50"
              >
                {t("analytics.hub.downloadXlsx")}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={() => void handleDownload("pdf")}
                className="flex cursor-pointer items-center rounded-md px-3 py-2 text-sm text-gray-700 outline-none hover:bg-gray-50 focus:bg-gray-50"
              >
                {t("analytics.hub.downloadPdf")}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </div>
  );
}
